import {
  isCloudSidebarEnvironment,
  type SavedEnvironmentRuntimeIndex,
} from "./sidebarThreadRuntimeIdentity";

/**
 * WHERE a thread ran — the single question the origin badge answers.
 *
 * This is the canonical rule. Surfaces that show a location glyph for a thread
 * resolve it here rather than deriving their own, because the row and its own
 * hover tooltip used to disagree: the row rendered a lone ServerIcon for
 * anything non-primary while the tooltip already split cloud from machine.
 *
 *  current-device — the machine you are looking at. Deliberately wears NO
 *                   glyph: a badge on every row says nothing, and upstream
 *                   t3code gates its icon on `isRemote` for the same reason.
 *  linked-device  — another of your machines (personal MacBook, Windows box,
 *                   an ssh host). Upstream's ServerIcon, unchanged.
 *  cloud          — a Forge Cloud VM (Sprite). The one addition over upstream,
 *                   which has no cloud runtime to distinguish.
 *
 * Deliberately NOT the 5-kind `EnvironmentKind` taxonomy: that names a laptop
 * apart from a desktop apart from an ssh host, which earns its keep on a
 * sidebar group header that shows the machine's name. A 12px badge beside a
 * branch chip cannot carry that distinction, and trying makes "somewhere else"
 * read as three unrelated states.
 */
export type ThreadOrigin = "current-device" | "linked-device" | "cloud";

export function resolveThreadOrigin(input: {
  readonly threadEnvironmentId: unknown;
  readonly currentEnvironmentId: unknown | null;
  /**
   * The saved-environment registry, so a runtime connected under CMMD's
   * durable `env_…` key still reads as cloud. Without it only the prefixed
   * locators do, and a live cloud row wore the linked-device glyph (FOR-36687).
   */
  readonly savedEnvironments?: SavedEnvironmentRuntimeIndex;
}): ThreadOrigin {
  // Cloud is decided by the environment identity alone, so it holds even when
  // we do not know which machine the user is sitting at. Checked first for
  // that reason: a sprite is in the cloud regardless of local state.
  if (isCloudSidebarEnvironment(input.threadEnvironmentId, input.savedEnvironments)) {
    return "cloud";
  }

  // An unknown current environment renders nothing rather than guessing
  // "somewhere else" — t3code Sidebar.tsx:937 reads
  // `currentEnvironmentId !== null && thread.env !== current`, so a null
  // current environment is not remote. Forge contradicts itself on this today
  // (ActiveThreadsSection.tsx:1150 matches upstream, threadLinkState.ts:49
  // inverts it); upstream wins.
  if (input.currentEnvironmentId === null) return "current-device";

  return input.threadEnvironmentId === input.currentEnvironmentId
    ? "current-device"
    : "linked-device";
}
